import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  DataGrid,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  QuickFilterTrigger,
  GridDensity,
  gridColumnLookupSelector,
  gridFilterActiveItemsSelector,
  GridFilterItem,
  GridFilterModel,
  useGridApiContext,
  useGridSelector,
  GridFilterListIcon,
} from '@mui/x-data-grid';
import {
  Typography,
  Tooltip,
  Menu,
  Badge,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Stack,
  Chip,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import capitalize from '@mui/utils/capitalize';

// Module augmentation for toolbar props
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    settings: Settings;
    onSettingsChange: React.Dispatch<React.SetStateAction<Settings>>;
    onRemoveFilter: (filterId: GridFilterItem['id']) => void;
    showFilterBar?: boolean;
    showSettings?: boolean;
    customTitle?: string;
  }
}

interface Settings {
  density?: GridDensity;
  showCellBorders?: boolean;
  showColumnBorders?: boolean;
}

type ToolbarProps = {
  settings: Settings;
  onSettingsChange: React.Dispatch<React.SetStateAction<Settings>>;
  onRemoveFilter: (filterId: GridFilterItem['id']) => void;
  showFilterBar?: boolean;
  showSettings?: boolean;
  customTitle?: string;
};

type OwnerState = {
  expanded: boolean;
};

// Styled components for quick filter
const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(
  ({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity']),
  }),
);

const StyledTextField = styled(TextField)<{
  ownerState: OwnerState;
}>(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState.expanded ? 260 : 'var(--trigger-width)',
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(['width', 'opacity']),
}));

// Settings configuration
const DENSITY_OPTIONS: { label: string; value: GridDensity }[] = [
  { label: 'Densidad compacta', value: 'compact' },
  { label: 'Densidad estándar', value: 'standard' },
  { label: 'Densidad cómoda', value: 'comfortable' },
];

const SETTINGS_STORAGE_KEY = 'treasury-datagrid-settings';

const SETTINGS_DEFAULT: Settings = {
  density: 'compact',
  showCellBorders: true, // No horizontal borders by default
  showColumnBorders: false, // Column borders enabled by default as originally requested
};

export function CustomToolbar(props: ToolbarProps) {
  const {
    settings,
    onSettingsChange,
    onRemoveFilter,
    showFilterBar = true,
    showSettings = true,
    customTitle = 'DataGrid',
  } = props;

  const apiRef = useGridApiContext();
  const activeFilters = useGridSelector(apiRef, gridFilterActiveItemsSelector);
  const columns = useGridSelector(apiRef, gridColumnLookupSelector);

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const exportMenuTriggerRef = useRef(null);
  const settingsMenuTriggerRef = useRef(null);

  return (
    <Toolbar sx={{ padding: 2, gap: 1 }}>
      {/* Title */}
      <Typography fontWeight="medium" sx={{ flex: 1, mx: 0.5 }}>
        {customTitle}
      </Typography>

      {/* Filter Bar - Active Filters */}
      {showFilterBar && activeFilters.length > 0 && (
        <Stack direction="row" sx={{ gap: 0.5, flex: 1, mr: 2 }}>
          {activeFilters.map((filter) => {
            const column = columns[filter.field];
            const field = column?.headerName ?? filter.field;
            const operator = apiRef.current.getLocaleText(
              `filterOperator${capitalize(filter.operator)}` as 'filterOperatorContains',
            );
            const isDate = column?.type === 'date';
            const value = isDate
              ? new Date(filter.value).toLocaleDateString('es-CO')
              : (filter.value ?? '');

            return (
              <Chip
                key={filter.id}
                label={`${field} ${operator} ${value}`}
                onDelete={() => onRemoveFilter(filter.id)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mx: 0.25 }}
              />
            );
          })}
        </Stack>
      )}

      {/* Core Toolbar Actions */}
      <Tooltip title="Columnas">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>

      <Tooltip title="Filtros">
        <FilterPanelTrigger
          render={(props, state) => (
            <ToolbarButton {...props} color="default">
              <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                <FilterListIcon fontSize="small" />
              </Badge>
            </ToolbarButton>
          )}
        />
      </Tooltip>

      <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />

      {/* Export Menu */}
      <Tooltip title="Exportar">
        <ToolbarButton
          ref={exportMenuTriggerRef}
          id="export-menu-trigger"
          aria-controls="export-menu"
          aria-haspopup="true"
          aria-expanded={exportMenuOpen ? 'true' : undefined}
          onClick={() => setExportMenuOpen(true)}
        >
          <FileDownloadIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>

      <Menu
        id="export-menu"
        anchorEl={exportMenuTriggerRef.current}
        open={exportMenuOpen}
        onClose={() => setExportMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            'aria-labelledby': 'export-menu-trigger',
          },
        }}
      >
        <ExportCsv render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
          Descargar como CSV
        </ExportCsv>
      </Menu>

      {/* Settings Menu */}
      {showSettings && (
        <>
          <Tooltip title="Configuración">
            <ToolbarButton
              ref={settingsMenuTriggerRef}
              id="settings-menu-trigger"
              aria-controls="settings-menu"
              aria-haspopup="true"
              aria-expanded={settingsMenuOpen ? 'true' : undefined}
              onClick={() => setSettingsMenuOpen(true)}
            >
              <SettingsIcon fontSize="small" />
            </ToolbarButton>
          </Tooltip>

          <Menu
            id="settings-menu"
            anchorEl={settingsMenuTriggerRef.current}
            open={settingsMenuOpen}
            onClose={() => setSettingsMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              list: {
                'aria-labelledby': 'settings-menu-trigger',
              },
            }}
          >
            {DENSITY_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() =>
                  onSettingsChange((currentSettings) => ({
                    ...currentSettings,
                    density: option.value,
                  }))
                }
              >
                <ListItemIcon>
                  {settings.density === option.value && <CheckIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={() =>
                onSettingsChange((currentSettings) => ({
                  ...currentSettings,
                  showColumnBorders: !currentSettings.showColumnBorders,
                }))
              }
            >
              <ListItemIcon>
                {settings.showColumnBorders && <CheckIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>Mostrar bordes de columnas</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                onSettingsChange((currentSettings) => ({
                  ...currentSettings,
                  showCellBorders: !currentSettings.showCellBorders,
                }))
              }
            >
              <ListItemIcon>
                {settings.showCellBorders && <CheckIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>Mostrar bordes de filas</ListItemText>
            </MenuItem>
          </Menu>
        </>
      )}

      {/* Quick Filter - Always Expanded */}
      <StyledQuickFilter defaultExpanded>
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title="Buscar" enterDelay={0}>
              <StyledToolbarButton
                {...triggerProps}
                ownerState={{ expanded: state.expanded }}
                color="default"
                aria-disabled={state.expanded}
              >
                <SearchIcon fontSize="small" />
              </StyledToolbarButton>
            </Tooltip>
          )}
        />
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              ownerState={{ expanded: state.expanded }}
              inputRef={ref}
              aria-label="Buscar en todos los campos"
              placeholder="Buscar..."
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label="Limpiar búsqueda"
                        material={{ sx: { marginRight: -0.75 } }}
                      >
                        <CancelIcon fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...controlProps.slotProps?.input,
                },
                ...controlProps.slotProps,
              }}
            />
          )}
        />
      </StyledQuickFilter>
    </Toolbar>
  );
}

// Hook for settings management with localStorage persistence
export const useDataGridSettings = (storageKey?: string): [Settings, React.Dispatch<React.SetStateAction<Settings>>] => {
  const key = storageKey || SETTINGS_STORAGE_KEY;
  
  const getInitialSettings = (): Settings => {
    try {
      const storedSettings = localStorage.getItem(key);
      return storedSettings ? JSON.parse(storedSettings) : SETTINGS_DEFAULT;
    } catch (error) {
      console.warn('Failed to load DataGrid settings from localStorage:', error);
      return SETTINGS_DEFAULT;
    }
  };

  const [settings, setSettings] = useState<Settings>(getInitialSettings());

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save DataGrid settings to localStorage:', error);
    }
  }, [settings, key]);

  return [settings, setSettings];
};

export default CustomToolbar;